import { UseFormRegisterReturn } from 'react-hook-form';
import { Dropdown, DropdownItem, MultiSelectDropdown } from '../molecules';

type TimezonePickerProps = {
  timezone?: string;
  updateTimezone: (timezone: string) => void;
};

const getTimezoneOffsetInMinutes = (timeZoneIdentifier: string) => {
  const date = new Date();
  // Format the date to include the long offset name
  const formattedString = date.toLocaleString('en-US', {
    timeZone: timeZoneIdentifier,
    timeZoneName: 'longOffset',
  });

  // Extract the offset part (e.g., "+05:00" or "-07:00")
  const match = formattedString.match(/([+-]\d+):(\d+)$/);
  if (match) {
    return match[0];
  }
  return '+00:00';
};

const formatTimezoneLabel = (timeZone: string) => {
  const offset = getTimezoneOffsetInMinutes(timeZone);
  return `(GMT${offset}) ${timeZone}`;
};

const timeZoneOptions = Intl.supportedValuesOf('timeZone')
  .map((tz) => ({
    label: formatTimezoneLabel(tz),
    value: tz,
  }))
  .sort((a, b) => {
    const offsetA = a.label.match(/([+-]\d+):(\d+)/);
    const offsetB = b.label.match(/([+-]\d+):(\d+)/);
    if (offsetA && offsetB) {
      return parseInt(offsetA[0]) - parseInt(offsetB[0]);
    }
    return 0;
  });

export const TimezonePicker = (props: TimezonePickerProps) => {
  const { timezone, updateTimezone } = props;
  // workaround for legacy user timezones that do not match Intl.supportedValuesOf
  // and will error if passed as timeZone option to date.toLocaleString
  // for these users we just do not populate
  const supportedTZFormat = !!timezone && Intl.supportedValuesOf('timeZone').includes(timezone);
  return (
    <Dropdown
      type='button'
      ariaLabel='Timezone Dropdown'
      buttonProps={{
        variant: 'secondary-gray',
        additionalClassnames: 'bg-white text-md text-gray-700 font-normal! px-3 py-2',
        size: 'lg',
        children: (
          <div className='p-4'>
            {supportedTZFormat ? formatTimezoneLabel(timezone) : 'Select a Timezone'}
          </div>
        ),
      }}
    >
      {timeZoneOptions.map((tz) => {
        return (
          <DropdownItem key={tz.value} onAction={() => updateTimezone(tz.value)}>
            {tz.label}
          </DropdownItem>
        );
      })}
    </Dropdown>
  );
};

type TimezoneMultiPickerProps = {
  updateTimezone: (timezone: string[]) => void;
  register: () => UseFormRegisterReturn<string>;
  filterable?: boolean;
  placeholder?: string;
  timezone?: string[];
};

export const TimezoneMultiPicker = (props: TimezoneMultiPickerProps) => {
  const { timezone, updateTimezone, register, placeholder, filterable } = props;

  return (
    <MultiSelectDropdown
      label='Timezone'
      options={timeZoneOptions.map((tz) => ({ label: tz.label }))}
      selectedItems={timezone ? timezone : []}
      onSelectionChange={(items) => updateTimezone(items)}
      register={register}
      placeholder={placeholder}
      filterable={filterable}
    />
  );
};
